import { useCallback } from "react";
import { listChangeSets, readChangeSetHead } from "../../../api/content/changeSetsApi";
import type { ChangeSetSummary } from "../../../api/content/reviewDecoders";
import { listSteps, listWorkflows, type ListedStep, type WorkflowSummary } from "../../../api/content/workflowsApi";
import { useSession } from "../../../session/sessionContext";
import { useLoad, type LoadState } from "../../../shared/loading/useLoad";

/**
 * What the Workflow editor's step picker offers (AU-6, F36): every step the project can
 * use at its latest model version, its own and those of the libraries it pins, and the
 * Workflows it may place as sub-workflows; plus the versions the user's open change sets
 * add, marked "proposed" with the change set. A proposed version can be placed in a draft
 * before it is merged; its review shows it.
 */
export type ProposedIn = Pick<ChangeSetSummary, "changeSetId" | "title">;

export interface PickerStep extends ListedStep {
  /** The user's open change set that proposes this version; `null` at the latest model version. */
  readonly proposedIn: ProposedIn | null;
}

export interface PickerWorkflow extends WorkflowSummary {
  readonly proposedIn: ProposedIn | null;
}

export interface PickerItems {
  readonly steps: readonly PickerStep[];
  readonly workflows: readonly PickerWorkflow[];
}

export async function loadPickerItems(projectId: string, userId: string | null, signal: AbortSignal): Promise<PickerItems> {
  const [steps, workflows, open, sentBack] = await Promise.all([
    listSteps(projectId, null, null, signal),
    listWorkflows(projectId, null, signal),
    listChangeSets(projectId, "open", signal),
    listChangeSets(projectId, "sent_back", signal),
  ]);
  const latest = new Set([...steps.steps.map((step) => step.reference), ...workflows.workflows.map((workflow) => workflow.reference)]);
  const own = [...open, ...sentBack].filter((changeSet) => changeSet.openedBy === userId);
  const proposed = await Promise.all(
    own.map(async (changeSet) => {
      const head = await readChangeSetHead(changeSet.changeSetId, signal);
      const [stepsThere, workflowsThere] = await Promise.all([listSteps(projectId, head, null, signal), listWorkflows(projectId, head, signal)]);
      const proposedIn = { changeSetId: changeSet.changeSetId, title: changeSet.title };
      return {
        steps: stepsThere.steps.filter((step) => !latest.has(step.reference)).map((step) => ({ ...step, proposedIn })),
        workflows: workflowsThere.workflows.filter((workflow) => !latest.has(workflow.reference)).map((workflow) => ({ ...workflow, proposedIn })),
      };
    }),
  );
  return {
    steps: [...steps.steps.map((step) => ({ ...step, proposedIn: null })), ...proposed.flatMap((change) => change.steps)],
    workflows: [...workflows.workflows.map((workflow) => ({ ...workflow, proposedIn: null })), ...proposed.flatMap((change) => change.workflows)],
  };
}

export function usePickerItems(projectId: string): { readonly state: LoadState<PickerItems>; readonly reload: () => void } {
  const { session } = useSession();
  const userId = session.status === "signedIn" ? session.session.account.userId : null;
  const load = useCallback((signal: AbortSignal) => loadPickerItems(projectId, userId, signal), [projectId, userId]);
  const { state, reload } = useLoad(load);
  return { state, reload };
}
